export const CheckCategory = {
  PEOPLE_CHECK: "people_check",
  POPULATION_CHECK: "population_check",
  SYSTEM_CHECK: "system_check",
  AWS_SYSTEM_CHECK: "aws_system_check",
  AZURE_SYSTEM_CHECK: "azure_system_check",
  GCP_SYSTEM_CHECK: "gcp_system_check",
  POLICY_CHECK: "policy_check",
  POLICY_CONTENT_CHECK: "policy_content_check",
  PASS_WITH_EVIDENCE: "pass_with_evidence",
  WORKFLOW_CHECK: "workflow_check",
};
export const SystemKey = {
  ZOHO_PEOPLE: "zohopeople",
};

export const NotificationTemplate = {
  CHECK_REGRESSION: "check_regression",
  CHECK_RUNS_MONITOR: "check_runs_monitor",
  // Value intentionally differs from the name, matching the cron.
  POLICY_TRAINING: "policy_ack_reminder",
  ROLE_CHANGE: "role_change",
  TERMINATION: "termination",
};

/** master_lookup keys of type `message_delivery_channel`. */
export const NotificationChannel = {
  EMAIL: "email",
};

/** Mirrors USER_TYPE in the API. */
export const UserType = {
  INTERNAL: "internal",
  EXTERNAL: "external",
};

export const IntegrationKey = {
  AWS: "aws",
  GCP: "gcp",
  AZURE: "azure",
  GITHUB: "github",
  BITBUCKET: "bitbucket",
};

export const IntegrationLabel = {
  [IntegrationKey.AWS]: "AWS",
  [IntegrationKey.GCP]: "GCP",
  [IntegrationKey.AZURE]: "Azure",
  [IntegrationKey.GITHUB]: "GitHub",
  [IntegrationKey.BITBUCKET]: "Bitbucket",
};

export const IntegrationType = {
  MANUAL: "MANUAL",
};
export const EvidenceStatus = {
  VALID: "valid",
  PROCESSING: "processing",
  INVALID: "invalid",
};
export const CheckStatus = {
  PASS: "pass",
  FAIL: "fail",
  PENDING: "pending",
  DISABLED: "disabled",
  WARNING: "warning",
};

export const CHECK_RUN_STATUS = {
  RUNNING: "running",
  COMPLETED: "completed",
  FAILED: "failed",
};

// Why an asset's currentStatus is "inactive". Null/absent while active.
// Mirrors ASSET_INACTIVITY_REASON in the API. Precedence there is
// disposed > out_of_scope > logged_out, so only one ever reaches the UI.
export const AssetInactivityReason = {
  DISPOSED: "disposed",
  OUT_OF_SCOPE: "out_of_scope",
  LOGGED_OUT: "logged_out",
};

export const AssetInactivityReasonLabel = {
  [AssetInactivityReason.DISPOSED]: "Disposed",
  [AssetInactivityReason.OUT_OF_SCOPE]: "Out of scope",
  [AssetInactivityReason.LOGGED_OUT]: "Logged out",
};

// Human label for an asset's inactivity reason; "" when unknown/active.
export const inactivityReasonLabel = (reason) =>
  AssetInactivityReasonLabel[String(reason || "").toLowerCase()] || "";

// One wording for what being out of scope means, in the two grammatical forms
// the UI needs — a single asset (details dialog, row tooltips) and several
// (bulk dialog, page help).
//
// The "security checks no longer run" clause is deliberately identical in both:
// it is the part a user is meant to recognise wherever it appears, so it must
// not be reworded in one place and not the other.
export const ASSET_OUT_OF_SCOPE_EFFECT =
  "This asset is out of scope — security checks no longer run.";
export const ASSETS_OUT_OF_SCOPE_EFFECT =
  "These assets are out of scope — security checks no longer run.";

export const CheckStatusLabel = {
  PASSING: "Passing",
  FAILING: "Failing",
  PENDING: "Pending",
  DISABLED: "Disabled",
  WARNING: "Warning",
};

export const statusFilterOptions = [
  { id: CheckStatus.PASS, label: CheckStatusLabel.PASSING },
  { id: CheckStatus.FAIL, label: CheckStatusLabel.FAILING },
  { id: CheckStatus.DISABLED, label: CheckStatusLabel.DISABLED },
  { id: CheckStatus.PENDING, label: CheckStatusLabel.PENDING },
];

export const TaskStatus = {
  OPEN: "open",
  RESOLVED: "resolved",
};

export const ExceptionScope = {
  GLOBAL: "global",
  RESOURCE: "resource",
  PASS_WITH_EVIDENCE_GLOBAL: "pass_with_evidence_global",
};

export const ExceptionEntityType = {
  EMPLOYEE: "employee",
  RESOURCE: "resource",
};

export const ControlEffectiveness = {
  EFFECTIVE: "Effective",
  INEFFECTIVE: "Ineffective",
};

export const SourceTitle = {
  CHOICE: "Choice",
  LIBRARY: "Library",
  MANUAL: "Manual",
};
export const dbRoles = {
  info_sec_officer: "info_sec_officer",
  people_ops: "people_operations_person",
  compliance_manager: "compliance_manager",
};

export const EmptyFields = {
  TITLE: "No title",
  DESCRIPTION: "No description",
  RISK_CATEGORY: "N/A",
  OWNER: "Not Assigned",
  SOURCE: "N/A",
  UNKNOWN: "UNKNOWN",
};

export const MasterLookupItemsType = {
  MESSAGE_TEMPLATE: "message_template",
  MESSAGE_EVENT_STATUS: "message_event_status",
  MESSAGE_DELIVERY_STATUS: "message_delivery_status",
  MESSAGE_DELIVERY_CHANNEL: "message_delivery_channel",
  RISK_SOURCE: "risk_source",
  RISK_CATEGORY: "risk_category",
  RISK_ASSESSMENT_STATUS: "risk_assessment_status",
  RISK_TREATMENT: "risk_treatment",
  RISK_SCORE: "risk_score",
  TRAINING_SECTION: "training_section",
  TRAINING_MODULE_STATUS: "training_module_status",
  TRAINING_SECTION_ATTEMPT_STATUS: "training_section_attempt_status",
  EMPLOYEE_SYNC_STATUS: "employee_sync_status",
  IN_SCOPE_USER_CATEGORY: "inscope_user_category",
  NOT_IN_SCOPE_USER_CATEGORY: "not_inscope_user_category",
  HRMS_PROVIDER: "hrms_provider",
  IDP_PROVIDER: "idp_provider",
  MDM_PROVIDER: "mdm_provider",
  CLOUD_PROVIDER: "cloud_provider",
  CUSTOMER_SUPPORT_PROVIDER: "customer_support_provider",
  EMAIL_PROVIDER: "email_provider",
  BG_VERIFICATION_PROVIDER: "bg_verification_provider",
  FILE_STORAGE_PROVIDER: "file_storage_provider",
  ATS_PROVIDER: "ats_provider",
  KNOWLEDGE_BASE_PROVIDER: "knowledge_base_provider",
  TASK_MANAGEMENT_PROVIDER: "task_management_provider",
  VCS_PROVIDER: "vcs_provider",
  VULNERABILITY_SCANNER: "vulnerability_scanner",
  INCIDENT_RESPONSE_PROVIDER: "incident_response_provider",
};

export const EmployeeSyncStatus = {
  SYNCED_EMPLOYEE: "synced_employee",
  DB_DISCOVERED_EMPLOYEE: "db_discovered_employee",
  IDP_DISCOVERED_EMPLOYEE: "idp_discovered_employee",
};

export const AccountCategoryKey = {
  ACTIVE_USER: "active_user",
  NON_PERSON: "non_person",
  OFFBOARDED_USER: "offboarded_user",
};

export const AssessmentStatus = {
  DRAFT: "draft",
  COMPLETED: "completed",
  REJECTED: "rejected",
};

export const ReviewDecisionStatus = {
  APPROVED: "approved",
  MODIFIED: "modify",
  REJECTED: "rejected",
};

export const DomainType = {
  PEOPLE: "people",
  INFRA: "infra",
  RISK: "risk",
  VCS: "vcs_provider",
};

export const DomainTypeLabel = {
  [DomainType.PEOPLE]: "People",
  [DomainType.INFRA]: "Infrastructure",
  [DomainType.RISK]: "Risks",
  [DomainType.VCS]: "Code Security",
};

export const EmploymentStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  TERMINATED: "terminated",
};

export const EmploymentStatusLabel = {
  ACTIVE: "Active",
  SERVICE_ACCOUNT: "Service account",
  OFFBOARDED_USER: "Offboarded user",
  TERMINATED: "Terminated",
};

export const ExternalUserStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
};

export const ExternalUserStatusLabel = {
  ACTIVE: "Active",
  INACTIVE: "Inactive",
};

export const ValidationMessages = {
  REQUIRED: "Required",
};

export const AuditType = {
  INTERNAL: "internal",
  EXTERNAL: "external",
};

export const FrameworkKey = {
  ISO_IEC_27001_2022: "iso_iec_27001_2022",
  SOC2: "SOC2",
  DPDP: "dpdp_2023",
};

export const AuditStatus = {
  ACTIVE: "active",
  COMPLETED: "completed",
  ARCHIVED: "archived",
};

export const AuditStatusLabel = {
  [AuditStatus.ACTIVE]: "Active",
  [AuditStatus.COMPLETED]: "Completed",
  [AuditStatus.ARCHIVED]: "Archived",
};

// People Check Categories
export const PeopleCheckCategory = {
  NEW_JOINER: "employee_new_joiner",
  ALL_ACTIVE: "employee_all_active",
  ALL_ACTIVE_NEW_JOINER: "employee_all_active_new_joiner",
};

// Comment System Enums
export const AnnotationCommentEntityType = {
  CONTROL: "control",
  EVIDENCE_REQUEST: "evidence_request",
};


export const CommentModuleTypeEnum = {
  AUDIT: "audit", 
};

// SOC 2 Framework Types
export const SOC2FrameworkType = {
  TYPE1: "SOC2_TYPE1",
  TYPE2: "SOC2_TYPE2",
};

export const AdminStatusType = {
  READY_FOR_AUDIT: "ready_for_audit",
  NOT_READY: "not_ready_for_audit",
};

export const AdminStatusTypeLabel = {
  [AdminStatusType.READY_FOR_AUDIT]: "Ready for Audit",
  [AdminStatusType.NOT_READY]: "Not Ready",
};

export const AuditorStatusType = {
  APPROVED: "approved",
  RED_FLAGGED: "red_flagged",
  MARK_AS_NOT_APPLICABLE: "mark_as_not_applicable",
};

export const AuditorStatusTypeLabel = {
  [AuditorStatusType.APPROVED]: "Approved",
  [AuditorStatusType.RED_FLAGGED]: "Red Flagged",
  [AuditorStatusType.MARK_AS_NOT_APPLICABLE]: "Mark as N/A",
};

export const EVIDENCE_REQUEST_STATUS = {
  TYPE: "evidence_request",
  KEY: {
    OPEN: "open",
    CLOSE: "close",
  },
};

// Commercial AWS regions offered when scoping an AWS integration's scans.
// Mirrors AWS_REGIONS in regodit-api/utils/enums.js, which validates the input.
export const AWS_REGIONS = [
  { value: "us-east-1", label: "US East (N. Virginia)" },
  { value: "us-east-2", label: "US East (Ohio)" },
  { value: "us-west-1", label: "US West (N. California)" },
  { value: "us-west-2", label: "US West (Oregon)" },
  { value: "af-south-1", label: "Africa (Cape Town)" },
  { value: "ap-east-1", label: "Asia Pacific (Hong Kong)" },
  { value: "ap-south-1", label: "Asia Pacific (Mumbai)" },
  { value: "ap-south-2", label: "Asia Pacific (Hyderabad)" },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)" },
  { value: "ap-northeast-2", label: "Asia Pacific (Seoul)" },
  { value: "ap-northeast-3", label: "Asia Pacific (Osaka)" },
  { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" },
  { value: "ap-southeast-2", label: "Asia Pacific (Sydney)" },
  { value: "ap-southeast-3", label: "Asia Pacific (Jakarta)" },
  { value: "ap-southeast-4", label: "Asia Pacific (Melbourne)" },
  { value: "ca-central-1", label: "Canada (Central)" },
  { value: "eu-central-1", label: "Europe (Frankfurt)" },
  { value: "eu-central-2", label: "Europe (Zurich)" },
  { value: "eu-north-1", label: "Europe (Stockholm)" },
  { value: "eu-south-1", label: "Europe (Milan)" },
  { value: "eu-south-2", label: "Europe (Spain)" },
  { value: "eu-west-1", label: "Europe (Ireland)" },
  { value: "eu-west-2", label: "Europe (London)" },
  { value: "eu-west-3", label: "Europe (Paris)" },
  { value: "il-central-1", label: "Israel (Tel Aviv)" },
  { value: "me-central-1", label: "Middle East (UAE)" },
  { value: "me-south-1", label: "Middle East (Bahrain)" },
  { value: "sa-east-1", label: "South America (São Paulo)" },
];
